<template>
    <div class="product-creator flex justify-center items-center relative w-full max-w-screen-xl h-full mx-auto px-6 py-12">

        <div class="relative w-full">

            <!-- Main container for page -->
            <div class="flex flex-col md:flex-row justify-between gap-8 md:gap-12">

                <!-- Video -->
                <div class="w-full md:w-1/2 md:max-w-[430px]" v-if="uploading || readyToProceed">

                    <div class="w-full max-w-[430px] mx-auto video-wrapper">
                        <video autoplay muted playsinline controls>
                            <source :src="videoSource" type="video/mp4">
                        </video>
                    </div>

                </div>

                <!-- Proceed -->
                <div class="hidden md:flex flex-grow justify-center items-center" v-if="readyToProceed">

                    <div class="text-center">
                        <div class="text-lg text-primary">{{ __('home::orders.images_ready') }}</div>

                        <Button
                            type="button"
                            @click="$emit('continue')"
                            class="py-4 xl:py-6 px-6 xl:px-16 xl:text-xl mt-8"
                        >
                            <span class="text-left">{{ __('home::orders.continue_to_editing') }}</span>
                            <ChevronRightIcon class="flex-shrink-0 w-6 xl:w-8 h-6 xl:h-8 inline-block ml-4" />
                        </Button>
                    </div>

                </div>

                <!-- Add files -->
                <div
                    class="relative w-full"
                    :class="[uploading ? 'hidden md:block' : '']"
                    v-if="! readyToProceed"
                >
                    <input
                        type="file"
                        class="absolute inset-0 z-50 m-0 p-0 w-full h-full outline-none opacity-0 cursor-pointer"
                        v-on:dragover="dragover = true"
                        v-on:dragleave="dragover = false"
                        v-on:drop="uploading = false"
                        ref="files"
                        accept="image/*"
                        multiple
                        @input="handleFileUploads"
                    >
                    <label for="file-upload">

                        <div
                            class="h-full mt-1 flex justify-center items-center px-6 py-12 lg:py-24 border-2 border-gray-300 border-dashed rounded-md"
                        >

                            <div class="space-y-1 text-center">

                                <!-- Uploading... -->
                                <div v-if="uploading">

                                    <div class="w-100 h-20">

                                        <svg class="max-auto h-16 w-16 animate-spin mx-auto text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>

                                    </div>

                                    <div class="flex text-lg text-gray-600">
                                        <p class="font-medium text-primary">
                                            <span v-if="uploadPercentage < 100">
                                                {{ __('home::orders.uploading_images') }}
                                                ({{ uploadPercentage }}%)
                                            </span>
                                            <span v-else>
                                                {{ __('home::orders.handling_images') }}
                                            </span>
                                        </p>
                                    </div>

                                </div>

                                <!-- Upload -->
                                <div v-else>

                                    <div class="w-100 h-20">
                                        <PhotographIcon class="mx-auto h-16 w-16 text-primary" />
                                    </div>

                                    <div class="flex text-lg justify-center text-gray-600" v-if="dragover">
                                        <p class="font-medium text-primary">
                                            {{ __('forms::labels.drop_images') }}
                                        </p>
                                    </div>
                                    <div class="text-lg text-gray-600" v-else>
                                        <p class="inline font-medium text-primary">
                                            {{ __('forms::labels.upload_images') }}
                                        </p>
                                        <p class="pl-1 hidden lg:inline">{{ __('forms::labels.or_drag_and_drop').toLowerCase() }}</p>
                                    </div>

                                    <p class="text-xs text-gray-500 pt-1">
                                        {{ __('forms::labels.allowed_image_files') }}
                                    </p>

                                </div>

                            </div>
                        </div>
                    </label>
                </div>

            </div>

            <!-- USP -->
            <div class="w-full max-w-[960px] mx-auto flex gap-x-4 justify-between mt-24">

                <div class="block w-1/2 text-center">
                    <img src="/media/icons/icon-hammer.svg" class="h-12 w-12 mb-0 block mx-auto" />
                    <div class="mt-3">
                        <div class="text-xs lg:text-base font-bold uppercase">
                            {{ __('home::index.usp_1_title') }}
                        </div>
                        <div class="text-xs lg:text-base mt-1">
                            {{ __('home::index.usp_1_description') }}
                        </div>
                    </div>
                </div>

                <div class="block w-1/2 text-center">
                    <img src="/media/icons/icon-pakke.svg" class="h-12 w-12 mb-0 block mx-auto" />
                    <div class="mt-3">
                        <div class="text-xs lg:text-base font-bold uppercase">
                            {{ __('home::index.fast_delivery') }}
                        </div>
                        <div class="text-xs lg:text-base mt-1">
                            {{ __('home::orders.delivery_at_latest') }}
                            <button type="button" class="inline font-bold border-b border-dashed border-black focus:outline-none" @click="deliveryInfoOpen = true">
                                {{ __('home::orders.on').toLowerCase() }} {{ nextDelivery }}
                            </button>
                        </div>
                    </div>
                </div>

            </div>

            <!-- Info -->
            <p class="w-full text-center text-gray-500 text-xs lg:text-base mt-20 whitespace-pre-line">
                {{ __('home::orders.info_on_upload_page') }}
            </p>

            <!--
            <div v-if="$page.props.__locale === 'da'" class="mt-20">
                <TrustBox />
            </div>
            -->

            <ProductGuideSection class="border-t border-b border-gray-200 mt-16" />

            <Faq class="mt-16 mb-8" />

        </div>

        <!-- Mobile sticky actions -->
        <div
            class="block md:hidden sticky bottom-0 left-0 w-full bg-primary-light border-t border-gray-200"
            v-if="uploading || readyToProceed"
        >
            <div class="p-6">
                <div
                    v-if="uploading"
                >
                    <svg class="h-8 w-8 animate-spin mx-auto text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>

                    <div class="w-full text-lg text-gray-600 mt-2 text-center">
                        <p class="font-medium text-primary">
                            <span v-if="uploadPercentage < 100">
                                {{ __('home::orders.uploading_images') }}
                                ({{ uploadPercentage }}%)
                            </span>
                            <span v-else>
                                {{ __('home::orders.handling_images') }}
                            </span>
                        </p>
                    </div>
                </div>

                <div
                    class="flex flex-grow justify-center items-center"
                    v-else-if="readyToProceed"
                >
                    <div class="text-center">
                        <div class="text-lg text-primary">{{ __('home::orders.images_ready') }}</div>

                        <Button
                            type="button"
                            @click="$emit('continue')"
                            class="py-4 xl:py-6 px-6 xl:px-16 xl:text-xl mt-4"
                        >
                            <span class="text-left">{{ __('home::orders.continue_to_editing') }}</span>
                            <ChevronRightIcon class="flex-shrink-0 w-6 xl:w-8 h-6 xl:h-8 inline-block ml-4" />
                        </Button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Make buttons beneath for these -->
        <StockPhotoSelector
            :open="stockPhotoSelectorOpen"
            :order="order"
            @close="stockPhotoSelectorOpen = false"
            @append-photos="appendPhotos($event)"
        />

        <SocialPhotoSelector
            :open="socialPhotoSelectorOpen"
            :order="order"
            @close="socialPhotoSelectorOpen = false"
            @append-photos="appendPhotos($event)"
        />

        <DeliveryInfo
            :open="deliveryInfoOpen"
            @close="deliveryInfoOpen = false"
        />

    </div>
</template>

<script>
    import axios from 'axios'
    import collect from 'collect.js'

    import {
        XIcon,
    } from '@heroicons/vue/solid'

    import {
        ChevronRightIcon,
        PlusIcon,
        PhotographIcon,
    } from '@heroicons/vue/outline'

    export default {
        components: {
            PlusIcon,
            XIcon,
            ChevronRightIcon,
            PhotographIcon,
        },
        props: {
            photos: Object,
            order: Object,
            nextDelivery: String,
        },
        data() {
            return {
                dragover: false,
                uploading: false,
                uploadPercentage: 0,
                readyToProceed: false,
                stockPhotoSelectorOpen: false,
                socialPhotoSelectorOpen: false,
                deliveryInfoOpen: false,
            }
        },
        computed: {
            videoSource() {
                return window.innerWidth >= 768
                    ? ('/media/' + this.$page.props.__media_folder + '/upload-desktop.mp4')
                    : ('/media/' + this.$page.props.__media_folder + '/upload-mobile.mp4');
            },
        },
        methods: {
            handleFileUploads() {

                var files = this.$refs.files.files;

                if (files.length == 0) {
                    return;
                }

                this.uploading = true;
                this.uploadPercentage = 0;

                var request = new FormData();

                for (var i = 0; i < files.length; i++) {
                    var file = files[i];
                    request.append('files[' + i + ']', file);
                }

                axios
                    .post(this.$route('home::orders.photos.upload', { uuid: this.order.uuid }), request, {
                        header: {
                            'Content-Type': 'multipart/form-data'
                        },
                        onUploadProgress: function(progressEvent) {
                            this.uploadPercentage = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
                        }.bind(this),
                    })
                    .then((res) => {
                        this.uploading = false;
                        this.readyToProceed = true;
                        this.appendPhotos(res.data);
                    });
            },
            appendPhotos(newPhotos) {
                var photos = this.photos;
                newPhotos.forEach(newPhoto => photos.push(newPhoto));
                this.$emit('update:photos', photos);

                this.$gtm.trackEvent({
                    event: 'UploadPhoto',
                    category: 'PhotoCreation',
                });
            },
        },
    }
</script>
